import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useThemeColors, getPrimaryButtonStyles } from '../hooks/useThemeColors';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { Alert, AlertDescription } from '../components/ui/alert';
import LoadErrorCard from '../components/LoadErrorCard';
import { toast } from 'sonner';
import { useAbortableLoad, isCanceled } from '../hooks/useAbortableLoad';
import {
  Users, Search, Upload, Loader2, FileText, ChevronRight, TrendingUp,
  AlertCircle, CheckCircle2, XCircle, Briefcase, X,
} from 'lucide-react';

const SemaforoDot = ({ semaforo }) => {
  const map = { verde: '#16a34a', ambar: '#f59e0b', rojo: '#dc2626' };
  const color = map[semaforo] || '#cbd5e1';
  return <span className="inline-block w-2.5 h-2.5 rounded-full" style={{ backgroundColor: color }} title={semaforo || 'Sin encuestas'} />;
};

const SEMAFORO_FILTROS = [
  { key: 'todos', label: 'Todos', color: null },
  { key: 'verde', label: 'Promotores', color: '#16a34a' },
  { key: 'ambar', label: 'Pasivos', color: '#f59e0b' },
  { key: 'rojo', label: 'Detractores', color: '#dc2626' },
  { key: 'sin', label: 'Sin encuestas', color: '#cbd5e1' },
];

const Cliente360ListPage = () => {
  const { api, isAdmin } = useAuth();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const themeColors = useThemeColors();

  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(false);
  const [clientes, setClientes] = useState([]);
  const [search, setSearch] = useState('');
  const [importing, setImporting] = useState(false);
  const [importResult, setImportResult] = useState(null);
  const [estadoSemaforo, setEstadoSemaforo] = useState('todos');
  const [puntoContacto, setPuntoContacto] = useState('');
  const [puntos, setPuntos] = useState([]);
  const [proyectoFiltro, setProyectoFiltro] = useState(searchParams.get('proyecto') || '');
  const [proyectoNombre, setProyectoNombre] = useState(searchParams.get('proyecto_nombre') || '');
  const fileInputRef = useRef(null);

  const loadClientes = useCallback(async (q = '', punto = '', proyecto = '', signal) => {
    setLoading(true);
    setLoadError(false);
    try {
      const params = {};
      if (q) params.search = q;
      if (punto) params.codigo_punto_contacto = punto;
      if (proyecto) params.codigo_proyecto = proyecto;
      const res = await api.get('/experiencia/clientes', { params, signal });
      setClientes(res.data || []);
    } catch (e) {
      if (isCanceled(e)) return;
      setLoadError(true);
      toast.error('Error al cargar los clientes');
    } finally {
      setLoading(false);
    }
  }, [api]);

  useAbortableLoad((signal) => {
    api.get('/experiencia/analytics/filtros', { signal }).then((r) => setPuntos(r.data?.puntos || [])).catch(() => {});
  }, [api]);

  // Debounce búsqueda (reacciona también a punto de contacto y proyecto)
  useEffect(() => {
    const controller = new AbortController();
    const t = setTimeout(() => loadClientes(search.trim(), puntoContacto, proyectoFiltro, controller.signal), 350);
    return () => { clearTimeout(t); controller.abort(); };
  }, [search, puntoContacto, proyectoFiltro, loadClientes]);

  const clearProyecto = () => {
    setProyectoFiltro('');
    setProyectoNombre('');
    const sp = new URLSearchParams(searchParams);
    sp.delete('proyecto');
    sp.delete('proyecto_nombre');
    setSearchParams(sp, { replace: true });
  };

  const handleImportClick = () => fileInputRef.current?.click();

  const clientesFiltrados = clientes.filter((c) => {
    if (estadoSemaforo === 'todos') return true;
    if (estadoSemaforo === 'sin') return !c.semaforo;
    return c.semaforo === estadoSemaforo;
  });
  const conteo = (key) => key === 'todos'
    ? clientes.length
    : clientes.filter((c) => key === 'sin' ? !c.semaforo : c.semaforo === key).length;

  const handleFileChange = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file) return;
    setImporting(true);
    setImportResult(null);
    try {
      const fd = new FormData();
      fd.append('file', file);
      const res = await api.post('/experiencia/clientes/importar/json', fd, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      setImportResult(res.data);
      if (!res.data.abortado) {
        toast.success(`Importación completa: ${res.data.clientes_insertados} clientes nuevos, ${res.data.proformas_insertadas} proformas nuevas`);
        loadClientes(search.trim(), puntoContacto, proyectoFiltro);
      } else {
        toast.error('Importación cancelada por errores');
      }
    } catch (err) {
      const detail = err.response?.data?.detail || 'Error al importar el archivo';
      setImportResult({ abortado: true, errores: [detail] });
      toast.error('Importación cancelada');
    } finally {
      setImporting(false);
    }
  };

  return (
    <div className="space-y-5 animate-fadeIn" data-testid="cliente360-list-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="font-heading text-2xl font-bold text-slate-900 flex items-center gap-2">
            <Users className="w-6 h-6" /> Cliente 360
          </h1>
          <p className="text-slate-500 text-sm mt-1">Vista integral de clientes, proformas y experiencia.</p>
        </div>
        {isAdmin() && (
          <>
            <input ref={fileInputRef} type="file" accept=".json" className="hidden" onChange={handleFileChange} data-testid="cliente-file-input" />
            <Button onClick={handleImportClick} disabled={importing} size="sm" className="text-white" data-testid="importar-clientes-btn" {...getPrimaryButtonStyles(themeColors)}>
              {importing ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Upload className="w-4 h-4 mr-2" />}
              Importar JSON
            </Button>
          </>
        )}
      </div>

      {/* Buscador */}
      <Card className="border-slate-200">
        <CardContent className="p-4 flex flex-col gap-3">
          <div className="flex flex-col sm:flex-row gap-3">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
              <Input
                className="pl-9"
                placeholder="Buscar por nombre, documento, correo o celular…"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                data-testid="cliente-search-input"
              />
            </div>
            <Select value={puntoContacto || '__todos__'} onValueChange={(v) => setPuntoContacto(v === '__todos__' ? '' : v)}>
              <SelectTrigger className="w-full sm:w-64" data-testid="punto-contacto-select">
                <SelectValue placeholder="Punto de contacto" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="__todos__" data-testid="punto-contacto-opt-todos">Todos los puntos</SelectItem>
                {puntos.map((p) => <SelectItem key={p.codigo} value={p.codigo} data-testid={`punto-contacto-opt-${p.codigo}`}>{p.nombre}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div className="flex flex-wrap items-center gap-2" data-testid="semaforo-filtros">
            {SEMAFORO_FILTROS.map((f) => (
              <button
                key={f.key}
                onClick={() => setEstadoSemaforo(f.key)}
                data-testid={`semaforo-filtro-${f.key}`}
                className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-medium border transition-colors ${estadoSemaforo === f.key ? 'bg-slate-900 text-white border-slate-900' : 'bg-white text-slate-600 border-slate-200 hover:bg-slate-50'}`}
              >
                {f.color && <span className="inline-block w-2 h-2 rounded-full" style={{ backgroundColor: f.color }} />}
                {f.label}
                <span className={`ml-0.5 ${estadoSemaforo === f.key ? 'text-slate-300' : 'text-slate-400'}`}>{conteo(f.key)}</span>
              </button>
            ))}
            {proyectoFiltro && (
              <Badge
                variant="secondary"
                className="inline-flex items-center gap-1.5 pl-3 pr-1.5 py-1 bg-blue-50 text-blue-700 border border-blue-200"
                data-testid="proyecto-filtro-badge"
              >
                <Briefcase className="w-3 h-3" />
                Proyecto: {proyectoNombre || proyectoFiltro}
                <button onClick={clearProyecto} className="ml-0.5 rounded-full hover:bg-blue-200/60 p-0.5" data-testid="proyecto-filtro-clear" title="Quitar filtro de proyecto">
                  <X className="w-3 h-3" />
                </button>
              </Badge>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Tabla */}
      <Card className="border-slate-200">
        <CardContent className="p-0">
          {loading ? (
            <div className="p-12 flex justify-center"><Loader2 className="w-6 h-6 animate-spin text-slate-400" /></div>
          ) : loadError ? (
            <LoadErrorCard onRetry={() => loadClientes(search.trim(), puntoContacto, proyectoFiltro)} testId="clientes-load-error" />
          ) : clientesFiltrados.length === 0 ? (
            <div className="p-12 text-center text-slate-500" data-testid="clientes-empty">
              <Users className="w-10 h-10 mx-auto mb-3 text-slate-300" />
              {clientes.length === 0
                ? ((search || puntoContacto || proyectoFiltro) ? 'No se encontraron clientes para este filtro.' : 'No hay clientes registrados. Importa el archivo cliente360.json.')
                : 'No hay clientes con ese estado de semáforo.'}
            </div>
          ) : (
            <>
              {/* Desktop */}
              <div className="hidden md:block overflow-x-auto">
                <Table className="[&_tbody_td]:py-1.5">
                  <TableHeader>
                    <TableRow className="table-header">
                      <TableHead className="w-8 text-center">●</TableHead>
                      <TableHead>Cliente</TableHead>
                      <TableHead>Documento</TableHead>
                      <TableHead className="hidden lg:table-cell">Contacto</TableHead>
                      <TableHead className="text-center">Proformas</TableHead>
                      <TableHead>Rótulos</TableHead>
                      <TableHead></TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {clientesFiltrados.map((c) => (
                      <TableRow
                        key={c.id}
                        className="table-row cursor-pointer"
                        onClick={() => navigate(`/admin/experiencia/clientes/${c.id}`)}
                        data-testid={`cliente-row-${c.id}`}
                      >
                        <TableCell className="text-center"><SemaforoDot semaforo={c.semaforo} /></TableCell>
                        <TableCell className="font-medium text-slate-900">{c.nombre}</TableCell>
                        <TableCell className="text-sm text-slate-600">
                          {c.tipo_documento && <span className="text-xs text-slate-400 mr-1">{c.tipo_documento}</span>}
                          {c.documento || '—'}
                        </TableCell>
                        <TableCell className="hidden lg:table-cell text-sm text-slate-500">
                          <div className="truncate max-w-[220px] leading-tight">{c.email || '—'}</div>
                          <div className="text-xs leading-tight">{c.celular || ''}</div>
                        </TableCell>
                        <TableCell className="text-center">
                          <Badge variant="outline" className="font-normal"><FileText className="w-3 h-3 mr-1" />{c.num_proformas}</Badge>
                        </TableCell>
                        <TableCell>
                          {c.es_inversionista && (
                            <Badge className="bg-amber-100 text-amber-800 hover:bg-amber-100 border-0 font-normal" data-testid={`cliente-inversionista-${c.id}`}>
                              <Briefcase className="w-3 h-3 mr-1" /> Inversionista
                            </Badge>
                          )}
                        </TableCell>
                        <TableCell><ChevronRight className="w-4 h-4 text-slate-400" /></TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>

              {/* Mobile */}
              <div className="md:hidden divide-y divide-slate-100">
                {clientesFiltrados.map((c) => (
                  <button
                    key={c.id}
                    onClick={() => navigate(`/admin/experiencia/clientes/${c.id}`)}
                    className="w-full text-left p-4 flex items-start justify-between gap-2"
                    data-testid={`cliente-card-${c.id}`}
                  >
                    <div className="min-w-0">
                      <p className="font-medium text-slate-900 flex items-center gap-2"><SemaforoDot semaforo={c.semaforo} /> {c.nombre}</p>
                      <p className="text-xs text-slate-500 mt-1">{c.tipo_documento} {c.documento || ''} · {c.email || c.celular || ''}</p>
                      <div className="flex items-center gap-2 mt-2">
                        <Badge variant="outline" className="font-normal text-xs"><FileText className="w-3 h-3 mr-1" />{c.num_proformas}</Badge>
                        {c.es_inversionista && <Badge className="bg-amber-100 text-amber-800 border-0 font-normal text-xs"><Briefcase className="w-3 h-3 mr-1" />Inversionista</Badge>}
                      </div>
                    </div>
                    <ChevronRight className="w-4 h-4 text-slate-400 shrink-0 mt-1" />
                  </button>
                ))}
              </div>
            </>
          )}
        </CardContent>
      </Card>

      {/* Resultado importación */}
      <Dialog open={!!importResult} onOpenChange={(o) => !o && setImportResult(null)}>
        <DialogContent className="max-w-lg max-h-[85vh] overflow-y-auto" data-testid="import-clientes-result-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              {importResult?.abortado
                ? <><XCircle className="w-5 h-5 text-red-500" /> Importación cancelada</>
                : <><CheckCircle2 className="w-5 h-5 text-green-600" /> Importación completada</>}
            </DialogTitle>
          </DialogHeader>
          {importResult && (
            <div className="space-y-4">
              {!importResult.abortado && (
                <div className="grid grid-cols-2 gap-3 text-center">
                  <div className="rounded-lg bg-green-50 p-3"><p className="text-2xl font-bold text-green-700">{importResult.clientes_insertados}</p><p className="text-xs text-green-600 mt-1">Clientes nuevos</p></div>
                  <div className="rounded-lg bg-blue-50 p-3"><p className="text-2xl font-bold text-blue-700">{importResult.clientes_actualizados}</p><p className="text-xs text-blue-600 mt-1">Clientes actualizados</p></div>
                  <div className="rounded-lg bg-green-50 p-3"><p className="text-2xl font-bold text-green-700">{importResult.proformas_insertadas}</p><p className="text-xs text-green-600 mt-1">Proformas nuevas</p></div>
                  <div className="rounded-lg bg-blue-50 p-3"><p className="text-2xl font-bold text-blue-700">{importResult.proformas_actualizadas}</p><p className="text-xs text-blue-600 mt-1">Proformas actualizadas</p></div>
                </div>
              )}
              {importResult.abortado && (
                <Alert variant="destructive"><AlertCircle className="h-4 w-4" /><AlertDescription>Se encontraron errores. No se importó nada. Corrige el archivo y reintenta.</AlertDescription></Alert>
              )}
              {importResult.errores?.length > 0 && (
                <div className="border border-red-200 rounded-lg max-h-64 overflow-y-auto" data-testid="import-clientes-errores">
                  <ul className="divide-y divide-red-100">
                    {importResult.errores.map((err, i) => <li key={i} className="px-3 py-2 text-sm text-red-700">{err}</li>)}
                  </ul>
                </div>
              )}
              <DialogFooter>
                <Button onClick={() => setImportResult(null)} className="text-white" {...getPrimaryButtonStyles(themeColors)} data-testid="import-clientes-cerrar-btn">Cerrar</Button>
              </DialogFooter>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default Cliente360ListPage;
